import type { ComponentType } from 'react'
import { Mail, MessageSquare, MessageCircle, Phone, Globe, Headset, User, Monitor } from 'lucide-react'
import {
  FacebookIcon, WhatsAppIcon, InstagramIcon, YouTubeIcon, XIcon, TelegramIcon,
} from '../../modules/conversations/BrandIcons.js'

/**
 * One place that answers "what does this conversation look like?" for every
 * channel and every interaction source, so the inbox toolbar, the Source
 * column and any future surface all show the same icon, label and color.
 */
export interface ChannelMeta {
  label: string
  icon: ComponentType<{ className?: string }>
  /** Foreground color for the glyph. */
  fg: string
  /** Tinted background for the icon tile. */
  bg: string
}

export const CHANNEL_META: Record<string, ChannelMeta> = {
  email: { label: 'Email', icon: Mail, fg: 'text-fuchsia-600', bg: 'bg-fuchsia-50' },
  sms: { label: 'SMS', icon: MessageCircle, fg: 'text-green-600', bg: 'bg-green-50' },
  whatsapp: { label: 'WhatsApp', icon: WhatsAppIcon, fg: 'text-emerald-600', bg: 'bg-emerald-50' },
  telegram: { label: 'Telegram', icon: TelegramIcon, fg: 'text-sky-500', bg: 'bg-sky-50' },
  facebook: { label: 'Facebook', icon: FacebookIcon, fg: 'text-blue-700', bg: 'bg-blue-50' },
  instagram: { label: 'Instagram', icon: InstagramIcon, fg: 'text-pink-600', bg: 'bg-pink-50' },
  youtube: { label: 'YouTube', icon: YouTubeIcon, fg: 'text-red-600', bg: 'bg-red-50' },
  x: { label: 'X', icon: XIcon, fg: 'text-sky-600', bg: 'bg-sky-50' },
  voice: { label: 'Voice', icon: Phone, fg: 'text-orange-600', bg: 'bg-orange-50' },
  chat: { label: 'Web', icon: MessageSquare, fg: 'text-violet-600', bg: 'bg-violet-50' },
  web: { label: 'Web', icon: Globe, fg: 'text-indigo-600', bg: 'bg-indigo-50' },
}

export const FALLBACK_CHANNEL: ChannelMeta = {
  label: 'Other', icon: MessageSquare, fg: 'text-slate-500', bg: 'bg-slate-100',
}

export function channelMeta(channel: string | undefined): ChannelMeta {
  return (channel ? CHANNEL_META[channel] : undefined) ?? FALLBACK_CHANNEL
}

/* Interaction sources are a separate axis from the transport channel: the same
   web channel can arrive via the agent portal, the client portal or a walk-in.
   When a source is on file it wins the label, and a handful get their own
   glyph; otherwise the channel's own identity is used. */
const SOURCE_OVERRIDES: Record<string, Pick<ChannelMeta, 'icon' | 'fg' | 'bg'>> = {
  AGENTPORTAL: { icon: Headset, fg: 'text-indigo-600', bg: 'bg-indigo-50' },
  CLIENTPORTAL: { icon: Monitor, fg: 'text-sky-600', bg: 'bg-sky-50' },
  WALKIN: { icon: User, fg: 'text-teal-600', bg: 'bg-teal-50' },
  CALL: { icon: Phone, fg: 'text-orange-600', bg: 'bg-orange-50' },
}

function normalizeKey(v: string): string {
  return v.toUpperCase().replace(/[^A-Z0-9]/g, '')
}

/** Resolved identity for a row's Source column — label plus a matching glyph. */
export function sourceMeta(source: string | undefined, channel: string | undefined): ChannelMeta {
  const base = channelMeta(channel)
  if (!source) return base
  const override = SOURCE_OVERRIDES[normalizeKey(source)]
  return { ...base, ...(override ?? {}), label: source }
}

/** Options for the toolbar's channel filter — "" means every channel. */
export const CHANNEL_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All channels' },
  ...Object.entries(CHANNEL_META).map(([value, m]) => ({ value, label: m.label })),
]
